import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { authGuest } from "../../store/actions/guestActions";
import { setRole, updateSession } from "../../store/actions/sessionsActions";

// Module-level lock to prevent duplicate login calls across StrictMode double-mounts
// This persists across component remounts within the same page load
let loginInProgress = false;
let lastLoginParams = null;

const SigneeLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const regTokenQuery = queryParams.get("token");
  const taskIDQuery = queryParams.get("ti");
  const notarizationIDQuery = queryParams.get("ni");
  const emailQuery = queryParams.get("email");
  const reasonQuery = queryParams.get("reason");
  const openReschedule = queryParams.get("open_reschedule") === "true";
  const registered =
    queryParams.get("u_reg") === "false"
      ? false
      : queryParams.get("u_reg") === "true"
        ? true
        : undefined;

  useEffect(() => {
    // Build parameters based on the available query parameters
    const params = {};
    if (
      regTokenQuery &&
      taskIDQuery &&
      notarizationIDQuery &&
      emailQuery &&
      reasonQuery &&
      registered !== undefined
    ) {
      Object.assign(params, {
        regTokenQuery,
        taskIDQuery,
        notarizationIDQuery,
        emailQuery,
        reasonQuery,
        registered,
        openReschedule,
      });
    }

    if (Object.keys(params).length > 0) {
      login(params);
    } else {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    regTokenQuery,
    taskIDQuery,
    notarizationIDQuery,
    emailQuery,
    reasonQuery,
    registered,
    openReschedule,
    navigate,
    dispatch,
  ]);

  const login = async (params, retryCount = 0) => {
    // Create a params key to detect same login attempt
    const paramsKey = JSON.stringify(params);

    // Prevent duplicate calls - check module-level lock
    if (loginInProgress && lastLoginParams === paramsKey) {
      return;
    }

    loginInProgress = true;
    lastLoginParams = paramsKey;

    try {
      const result = await dispatch(authGuest(params));
      if (result && result?.data?.success === true) {
        const url = result.data.data.url;
        const isReusedSession = result.data.data.reused_session === true;
        delete result.data.data.url;
        delete result.data.data.reused_session;

        // If this is a reused session and we're already on the target page,
        // don't navigate (which would cause component remount and new WebSocket)
        const currentPath = window.location.pathname;
        if (isReusedSession && currentPath === url) {
          // Already authenticated and on the correct page - do nothing
          return;
        }

        sessionStorage.setItem(
          "__nocCreator__r_rid",
          JSON.stringify(["guest"]),
        );
        sessionStorage.setItem("exp", result?.data?.data?.expiry);

        await dispatch(setRole("guest"));

        if (isReusedSession) {
          // For reused sessions, just navigate without updating session state
          // This prevents WebSocket reconnection
          navigate(url, { replace: true });
        } else {
          // New session - update session state and navigate
          await dispatch(updateSession(result?.data?.data))
            .then(() => {
              navigate(url);
            })
            .catch((error) => {
              console.error("Session update failed", error);
              navigate("/error");
            });
        }
      } else if (result?.conflict) {
        if (result?.alreadyLoggedIn) {
          // User is already logged in on another device - don't retry
          // Show an alert and redirect to home
          alert(
            result?.message || "You are already logged in on another device.",
          );
          navigate("/");
          return;
        } else if (retryCount < 2) {
          // Another login attempt is in progress (e.g., email client preview)
          // Wait a moment and retry
          loginInProgress = false;
          setTimeout(() => {
            login(params, retryCount + 1);
          }, 2000);
          return;
        } else {
          navigate("/");
        }
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      // Reset lock after a short delay to prevent immediate re-triggering
      // but allow new login attempts on actual page reloads
      setTimeout(() => {
        loginInProgress = false;
        lastLoginParams = null;
      }, 3000);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <CircularProgress color="warning" size="10rem" />
    </Box>
  );
};

export default SigneeLogin;
